import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/Modal';
import Input from '@/components/common/Input';
import ActionButton from '@/components/common/ActionButton';
import { ProjectStatus } from '@/types';
import { useCreateProjectMutation } from '@/store/api/apiSlice';
import { toast } from 'react-toastify';
import { getApiError } from '@/utils/errorUtils';
import { FiPlus, FiX } from 'react-icons/fi';

interface ProjectCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const today = new Date().toISOString().slice(0, 10);

export default function ProjectCreationModal({ isOpen, onClose, onCreated }: ProjectCreationModalProps) {
  const { t } = useTranslation();
  const [createProject, { isLoading }] = useCreateProjectMutation();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>(ProjectStatus.PLANNING);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [color, setColor] = useState('#1D4ED8');

  const reset = () => {
    setName('');
    setKey('');
    setDescription('');
    setStatus(ProjectStatus.PLANNING);
    setStartDate(today);
    setEndDate('');
    setColor('#1D4ED8');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || !key.trim() || !description.trim() || !startDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const normalizedKey = key.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (normalizedKey.length < 2 || normalizedKey.length > 10) {
      toast.error('Project key must be 2-10 uppercase letters/numbers');
      return;
    }

    try {
      await createProject({
        name: name.trim(),
        key: normalizedKey,
        description: description.trim(),
        status,
        startDate: new Date(startDate).toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        color,
      }).unwrap();

      toast.success('Project created successfully');
      onCreated?.();
      handleClose();
    } catch (error: any) {
      toast.error(getApiError(error, 'Failed to create project'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('projectsPage.modalTitle')} size="md" closeOnOverlayClick={false}>
      <div className="space-y-4">
        <Input
          label={t('projectsPage.projectName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Platform Migration"
          required
          fullWidth
        />

        <Input
          label={t('projectsPage.projectKey')}
          value={key}
          onChange={(e) => setKey(e.target.value.toUpperCase())}
          placeholder="MIG"
          helperText="2-10 uppercase letters/numbers"
          required
          fullWidth
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('projectsPage.description')} <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            className="co-taskr-input w-full min-h-[100px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this project delivering?"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
            <select
              className="co-taskr-input w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value as ProjectStatus)}
            >
              <option value={ProjectStatus.PLANNING}>Planning</option>
              <option value={ProjectStatus.ACTIVE}>Active</option>
              <option value={ProjectStatus.ON_HOLD}>On Hold</option>
              <option value={ProjectStatus.COMPLETED}>Completed</option>
              <option value={ProjectStatus.ARCHIVED}>Archived</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectsPage.color')}</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-full rounded-lg border border-gray-300 bg-white px-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('projectsPage.startDate')}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            fullWidth
          />
          <Input
            label={t('projectsPage.endDate')}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <ActionButton variant="ghost" icon={FiX} onClick={handleClose}>{t('common.cancel')}</ActionButton>
          <ActionButton variant="primary" icon={FiPlus} onClick={handleSubmit} isLoading={isLoading}>
            {t('common.createProject')}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
